import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGqlClient } from "~/libs/gql";
import { graphql } from "~/types/__generated/gql";
import { GetProcessesQuery } from "~/types/__generated/gql/graphql";
import { sortBySortingIndexAndUpdatedAt } from "~/utils/sort-by-sorting-index-and-updated-at";
import { sortBySortingIndex } from "~/utils/sorting-array-result";

const query = /* GraphQL */ `
	query GetProcesses {
		getProcesses {
			_id
			createdAt
			updatedAt
			leadTime
			proccessRemovalDuration
			name
			sortingIndex
			_files {
				_id
				createdAt
				updatedAt
				customFields {
					customField
					textValue
					selectValues
					dateTimeValue

					_customField {
						name
						type
					}
				}
				processPaths {
					process
					leadTime
					proccessRemovalDuration
				}
				processLastUpdatedAt
				isProcessed
				products {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				sortingIndex
				counterConfigs {
					file {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}

					invoice {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}
					monthlyStatement {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}
				}
				issueDate
				dueDate
				receipt
				paymentVerifications
				fileSuffixComment
				receiptSuffixComment
				receiptNote

				_receipt {
					isFullyPaid
					payments {
						paidAt
						paymentVerificationId
						payments {
							payment_type
							amount
							bank_received_date
						}
					}
				}
				_paymentVerifications {
					_id
					createdAt
					updatedAt
					user
					attachment {
						name
						size
						url
						extension
					}
					status
					receivedAmount
					receivedDate
					paymentUploadStatus

					counterConfig {
						file {
							counter
							index
							increment

							_counter {
								_id
								type
								prefix
								suffix_digit
								isPredefinedFormat
							}
						}
					}
				}

				_process {
					_id
					createdAt
					updatedAt
					name
				}
				_user {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
				_client {
					_id
					createdAt
					updatedAt
					name
				}
				_agent {
					_id
					createdAt
					updatedAt
					name
				}
				_assignees {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
			}
		}
	}
`;

export type GetProcessesResults = GetProcessesQuery["getProcesses"];

export const getProcessesQueryKey = () => ["getProcesses"];

export const useGetProcesses = (enabled?: boolean) => {
	const queryClient = useQueryClient();

	return useQuery({
		enabled: enabled ?? true,
		queryKey: getProcessesQueryKey(),
		queryFn: async () =>
			apiGqlClient.request(graphql(query)).then((res) => res.getProcesses),
		onSuccess: (data) => {
			queryClient.setQueryData<GetProcessesResults>(
				getProcessesQueryKey(),
				sortProcessesAndFiles(data),
			);
		},
	});
};

export const sortProcessesAndFiles = (array: GetProcessesResults) =>
	array.sort(sortBySortingIndexAndUpdatedAt).map((process) => ({
		...process,
		_files: process._files.sort(sortBySortingIndex),
	}));

export const useGetProcessesCache = () => {
	return useGetProcesses(false);
};
